import React, { useState } from 'react';
import apiService from '../../services/apiService';
import Modal from '../ui/Modal';
import { useI18n } from '../../utils/i18n';

// #530 (Scheibe 1 aus #310): Ticket uebergeben -- Zielperson, optionaler
// Kommentar und die Planungsfrage (vergangene Zeit genutzt? zukuenftige
// Planung entfernen?). Code unveraendert aus TicketDetail.js uebernommen,
// Muster aus #312/#471/#528.
//
// Den Hook ruft TicketDetail auf, der Dialog rendert nur. Geoeffnet wird er
// ueber den Knopf im Kopf von TicketDetail, und die Paste- und Drag-Sperren
// dort lesen showTransferModal.
export function useUebergabe({ ticket, currentTicket, onClose, onTicketUpdate, setError, setHasChanges, getCurrentUserEmail }) {
  const { t } = useI18n();
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAssignee, setTransferAssignee] = useState('');
  const [transferComment, setTransferComment] = useState('');
  const [transferring, setTransferring] = useState(false);
  // Übergabe-Planungsfelder (optional änderbar)
  const [transferDate, setTransferDate] = useState('');
  const [transferStart, setTransferStart] = useState('');
  const [transferEnd, setTransferEnd] = useState('');
  const [transferPrefilled, setTransferPrefilled] = useState(false);
  const [transferValidationError, setTransferValidationError] = useState(null);
  const [transferPastPlannedExists, setTransferPastPlannedExists] = useState(false);
  const [transferUsedPastTime, setTransferUsedPastTime] = useState(null); // 'yes' | 'no' | null
  const [transferFuturePlannedOnly, setTransferFuturePlannedOnly] = useState(false);

  const handleOpenTransferModal = () => {
    setError(null);
    setTransferComment('');
    setTransferAssignee(currentTicket.assignedTo || '__UNASSIGNED__');
    // Prefill Planungszeiten und Past-Check
    try {
      setTransferValidationError(null);
      setTransferUsedPastTime(null);
      const hasPlanning = !!(currentTicket?.plannedStartDateTime && currentTicket?.plannedEndDateTime);
      if (hasPlanning) {
        const s = new Date(currentTicket.plannedStartDateTime);
        const e = new Date(currentTicket.plannedEndDateTime);
        const toDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        const toTime = (d) => `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
        const nowMs = Date.now();
        const isPastOrOngoing = (e.getTime() <= nowMs) || (s.getTime() <= nowMs);
        setTransferPastPlannedExists(isPastOrOngoing);
        const isFutureOnly = (!isPastOrOngoing && s.getTime() > nowMs);
        setTransferFuturePlannedOnly(isFutureOnly);
        if (isFutureOnly) {
          setTransferDate('');
          setTransferStart('');
          setTransferEnd('');
          setTransferPrefilled(false);
        } else {
          setTransferDate(toDate(s));
          setTransferStart(toTime(s));
          setTransferEnd(toTime(e));
          setTransferPrefilled(true);
        }
      } else {
        setTransferDate('');
        setTransferStart('');
        setTransferEnd('');
        setTransferPrefilled(false);
        setTransferPastPlannedExists(false);
        setTransferFuturePlannedOnly(false);
      }
    } catch (_) {
      setTransferDate('');
      setTransferStart('');
      setTransferEnd('');
      setTransferPrefilled(false);
      setTransferPastPlannedExists(false);
      setTransferFuturePlannedOnly(false);
    }
    setShowTransferModal(true);
  };

  const handleTransferTicket = async (e) => {
    e.preventDefault();
    if (!transferAssignee) {
      setError(t('ticketDetail.selectTargetPersonError', 'Bitte wählen Sie eine Zielperson aus.'));
      return;
    }

    try {
      setTransferring(true);

      if (transferComment.trim()) {
        await apiService.addCommentToTicket(ticket.id, transferComment.trim());
      }

      // Optional: Planungszeiten anpassen/erstellen wie im Abschließen-Modal
      const anyTimeProvided = !!(transferDate || transferStart || transferEnd);
      const hasExistingPlanning = !!(currentTicket?.isPlanned || currentTicket?.plannedStartDateTime || currentTicket?.plannedEndDateTime || currentTicket?.calendarEventId);
      const baseTicketId = currentTicket?.originalTicketId || ticket?.originalTicketId || ticket.id;
      if (transferPastPlannedExists && transferUsedPastTime === 'no') {
        try {
          await apiService.unplanTicket(baseTicketId);
        } catch (unplanErr) {
          console.error('Error unplanning past entries during transfer:', unplanErr);
        }
      } else if (anyTimeProvided) {
        if (!transferDate || !transferStart || !transferEnd) {
          setTransferValidationError(t('ticketDetail.completeMissingFields', 'Bitte Datum, Startzeit und Endzeit angeben.'));
          return;
        }
        setTransferValidationError(null);
        const startLocal = new Date(`${transferDate}T${transferStart}`);
        const endLocal = new Date(`${transferDate}T${transferEnd}`);
        if (Number.isNaN(startLocal.getTime()) || Number.isNaN(endLocal.getTime())) {
          setTransferValidationError(t('ticketDetail.completeInvalidFields', 'Ungültiges Datum oder Uhrzeit.'));
          return;
        }
        if (endLocal <= startLocal) {
          setTransferValidationError(t('ticketDetail.completeEndBeforeStart', 'Die Endzeit muss nach der Startzeit liegen.'));
          return;
        }

        const planningData = {
          startDateTime: startLocal.toISOString(),
          endDateTime: endLocal.toISOString(),
          isPlanned: true
        };
        const me = getCurrentUserEmail();
        if (me) planningData.assignedTo = me;

        try {
          if (hasExistingPlanning) {
            if (currentTicket?.planningId) planningData.planningId = currentTicket.planningId;
            await apiService.updateTicketPlanning(baseTicketId, planningData);
          } else {
            await apiService.planTicket(baseTicketId, planningData);
          }
        } catch (planErr) {
          console.error('Error updating/creating planning during transfer:', planErr);
          setError(t('ticketDetail.transferPlanningError', 'Fehler beim Aktualisieren/Erstellen des Kalendereintrags vor der Übergabe.'));
          return;
        }
      } else if (!transferPastPlannedExists && hasExistingPlanning && transferFuturePlannedOnly) {
        // Nur zukünftige Planung vorhanden -> vor der Übergabe entfernen (wie "Nein")
        try {
          await apiService.unplanTicket(baseTicketId);
        } catch (unplanErr) {
          console.error('Error unplanning future entries during transfer:', unplanErr);
        }
      }

      // Ticket-Übergabe ausführen
      const normalizedAssignee = transferAssignee === '__UNASSIGNED__' ? '' : transferAssignee;
      const updateResponse = await apiService.updateTicket(ticket.id, {
        assignedTo: normalizedAssignee
      });
      const updated = updateResponse?.ticket || { ...currentTicket, assignedTo: normalizedAssignee };
      if (onTicketUpdate) {
        try { onTicketUpdate(updated); } catch (_) {}
      }

      setHasChanges('transferred');
      setShowTransferModal(false);
      onClose('transferred');
    } catch (err) {
      if (err?.abgebrochen) return; // #554: Rueckfrage abgelehnt, Uebergabe-Dialog bleibt offen
      console.error('Error transferring ticket:', err);
      setError('Fehler beim Übergeben des Tickets');
    } finally {
      setTransferring(false);
    }
  };

  return {
    showTransferModal, setShowTransferModal, transferAssignee, setTransferAssignee, transferComment, setTransferComment, transferring,
    transferDate, setTransferDate, transferStart, setTransferStart, transferEnd, setTransferEnd, transferPrefilled,
    transferValidationError, transferPastPlannedExists, transferUsedPastTime, setTransferUsedPastTime, handleOpenTransferModal, handleTransferTicket
  };
}

export default function UebergabeModal({ uebergabe, currentTicket, availableEmails }) {
  const { t } = useI18n();
  const {
    showTransferModal, setShowTransferModal, transferAssignee, setTransferAssignee, transferComment, setTransferComment, transferring, transferDate, setTransferDate, transferStart, setTransferStart, transferEnd, setTransferEnd, transferPrefilled, transferValidationError, transferPastPlannedExists, transferUsedPastTime, setTransferUsedPastTime, handleTransferTicket
  } = uebergabe;
  return (
    <>
        {/* TRANSFER TICKET MODAL */}
        {showTransferModal && (
        <Modal
          open
          onClose={() => setShowTransferModal(false)}
          title={t('ticketDetail.transfer', 'Übergeben')}
          className="transfer-ticket-modal"
          maxWidth="700px"
        >

              <form onSubmit={handleTransferTicket} className="comment-form">
                <div className="form-body">
                  <div className="form-group">
                    <label htmlFor="transfer-assigned-to">{t('ticketDetail.transferAssignee', 'Zielperson')}</label>
                    <select
                      id="transfer-assigned-to"
                      value={transferAssignee}
                      onChange={(e) => setTransferAssignee(e.target.value)}
                      className="form-select"
                      disabled={transferring}
                    >
                      <option value="">{t('common.pleaseSelect', 'Bitte auswählen')}</option>
                      {!currentTicket?.projectId && !currentTicket?.subprojectId && (
                        <option value="__UNASSIGNED__">{t('dashboard.unassigned', 'Nicht zugewiesen')}</option>
                      )}
                      {availableEmails.map(email => (
                        <option key={email} value={email}>{email}</option>
                      ))}
                    </select>
                  </div>

                  {/* Quellperson (aktueller Bearbeiter) */}
                  <div className="form-group">
                    <label>{t('ticketDetail.sourceAssignee', 'Quellperson')}</label>
                    <div className="form-static" style={{ padding: '8px 10px', background: 'var(--color-bg)', borderRadius: 4, border: '1px solid var(--color-border-light)' }}>
                      {currentTicket?.assignedTo || t('dashboard.unassigned', 'Nicht zugewiesen')}
                    </div>
                  </div>

                  {/* (Felder verschoben unter die Frage) */}

                  {/* Frage zu vergangenen Zeiten */
                  }
                  {transferPastPlannedExists && (
                    <div className="form-group" style={{ marginTop: 10 }}>
                      <label>{t('ticketDetail.usedPastTimeQuestion', 'Geplante vergangene Zeit genutzt?')}</label>
                      <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="radio"
                            name="usedPastTime"
                            value="yes"
                            checked={transferUsedPastTime === 'yes'}
                            onChange={() => setTransferUsedPastTime('yes')}
                            disabled={transferring}
                          />
                          {t('common.yes', 'Ja')}
                        </label>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="radio"
                            name="usedPastTime"
                            value="no"
                            checked={transferUsedPastTime === 'no'}
                            onChange={() => setTransferUsedPastTime('no')}
                            disabled={transferring}
                          />
                          {t('common.no', 'Nein')}
                        </label>
                      </div>
                      <div className="field-hint" style={{ color: 'var(--color-text-muted)', fontSize: 12, marginTop: 4 }}>
                        {t('ticketDetail.usedPastTimeHint', 'Bei Nein werden vergangene Kalendereinträge zu diesem Ticket aus Ihrem Kalender entfernt.')} 
                      </div>
                    </div>
                  )}

                  {/* Planungsfelder unterhalb der Frage anzeigen (nur wenn Vergangenheit/jetzt) */}
                  {transferValidationError && (
                    <div className="error-message" style={{ marginTop: 8 }}>
                      {transferValidationError}
                    </div>
                  )}
                  {transferPastPlannedExists && (
                    <div className="form-row" style={{ marginTop: 8 }}>
                      <div className="form-group">
                        <label htmlFor="transfer-date">{t('ticketDetail.completeDate', 'Datum')}</label>
                        <input
                          id="transfer-date"
                          type="date"
                          value={transferDate}
                          onChange={(e) => setTransferDate(e.target.value)}
                          className="form-input"
                          disabled={transferring}
                          placeholder="YYYY-MM-DD"
                          style={{ backgroundColor: (!transferPrefilled && !transferDate) ? 'var(--color-field-empty)' : undefined }}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="transfer-start">{t('ticketDetail.completeStart', 'Startzeit')}</label>
                        <input
                          id="transfer-start"
                          type="time"
                          value={transferStart}
                          onChange={(e) => setTransferStart(e.target.value)}
                          className="form-input"
                          disabled={transferring}
                          placeholder="08:00"
                          style={{ backgroundColor: (!transferPrefilled && !transferStart) ? 'var(--color-field-empty)' : undefined }}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="transfer-end">{t('ticketDetail.completeEnd', 'Endzeit')}</label>
                        <input
                          id="transfer-end"
                          type="time"
                          value={transferEnd}
                          onChange={(e) => setTransferEnd(e.target.value)}
                          className="form-input"
                          disabled={transferring}
                          placeholder="09:00"
                          style={{ backgroundColor: (!transferPrefilled && !transferEnd) ? 'var(--color-field-empty)' : undefined }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="field-hint" style={{ color: 'var(--color-text-muted)', fontSize: 12, marginTop: 4 }}>
                    {t('ticketDetail.transferHint', 'Wenn Sie Zeiten ändern, wird Ihr Kalendereintrag vor der Übergabe aktualisiert/erstellt.')}
                  </div>

                  <textarea
                    value={transferComment}
                    onChange={(e) => setTransferComment(e.target.value)}
                    placeholder={t('ticketDetail.optionalComment', 'Optionalen Kommentar eingeben...')}
                    rows="6"
                    disabled={transferring}
                    className="form-textarea"
                    style={{ marginTop: 10 }}
                  />
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowTransferModal(false)}
                    className="btn btn-secondary"
                    disabled={transferring}
                  >
                    {t('ticketDetail.cancel', 'Abbrechen')}
                  </button>
                  <button
                    type="submit"
                    disabled={transferring || !transferAssignee || (transferPastPlannedExists && transferUsedPastTime === null)}
                    className="btn btn-primary"
                  >
                    {transferring ? t('ticketDetail.adding', 'Hinzufügen...') : t('ticketDetail.transfer', 'Übergeben')}
                  </button>
                </div>
              </form>
        </Modal>
        )}
    </>
  );
}
